import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getCustomerMailers,
    getGroupMailerTypes,
} from '@/modules/Customer/services'
import { CustomerMailer, MailerType } from '@/modules/Customer/types'
import {
    getInvoicePrefillEmail,
    sendInvoiceMail,
} from '@/modules/Invoice/services'
import { ModalProps } from '@/types/shared'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'

export interface SendMailModalProps extends ModalProps {
    invoiceID: number
}

interface SendMailFormData {
    recipients: string
    cc: string
    bcc: string
}

/**
 * Helper function to get the best mailer based on priority:
 * 1. Invoice mailer group (mailer_type: 'invoice')
 * 2. Default mailer group (mailer_type: 'default')
 * 3. Any other customer mailer
 */
const getMailerByPriority = (
    mailers: CustomerMailer[] | undefined,
    mailerTypes: MailerType[] | undefined
): CustomerMailer | null => {
    if (!mailers || mailers.length === 0) return null

    // Find mailer type IDs
    const invoiceMailerTypeId = mailerTypes?.find(
        (type) => type.mailer_type === 'invoice'
    )?.mailer_type_id
    const defaultMailerTypeId = mailerTypes?.find(
        (type) => type.mailer_type === 'default'
    )?.mailer_type_id

    // Priority 1: Invoice mailer group
    if (invoiceMailerTypeId) {
        const invoiceMailer = mailers.find((mailer) =>
            mailer.mailer_type_ids.includes(invoiceMailerTypeId)
        )
        if (invoiceMailer) return invoiceMailer
    }

    // Priority 2: Default mailer group
    if (defaultMailerTypeId) {
        const defaultMailer = mailers.find((mailer) =>
            mailer.mailer_type_ids.includes(defaultMailerTypeId)
        )
        if (defaultMailer) return defaultMailer
    }

    // Priority 3: First available mailer
    return mailers[0]
}

const Send: React.FC<SendMailModalProps> = ({ invoiceID, onClose, open }) => {
    const { t } = useTranslation()
    const { data: prefillData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICE_PREFILL_EMAIL, invoiceID],
        queryFn: () => getInvoicePrefillEmail(invoiceID),
        enabled: !!invoiceID,
    })

    // Fetch customer mailers
    const { data: customerMailersData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMERS_MAILERS,
            prefillData?.result?.customer_id,
        ],
        queryFn: () =>
            getCustomerMailers({
                customerID: prefillData?.result?.customer_id!,
            }),
        enabled: !!prefillData?.result?.customer_id && open,
    })

    // Fetch mailer types
    const { data: mailerTypesData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_MAILERS],
        queryFn: () => getGroupMailerTypes(),
        enabled: open,
    })

    const { mutateAsync: sendMail, isPending: isSendingMail } =
        useMutationWithHandler(
            { mutationFn: sendInvoiceMail },
            {
                reactQueryKeys: QUERY_KEYS.INVOICES,
                successMessage: t('send-mail-success'),
                errorMessage: 'Có lỗi xảy ra khi gửi email',
                onSuccess: () => {
                    onClose()
                },
            }
        )

    const formControl = useForm<SendMailFormData>({
        defaultValues: {
            recipients: '',
            cc: '',
            bcc: '',
        },
    })

    // Calculate the best mailer based on priority
    const prioritizedMailer = useMemo(() => {
        return getMailerByPriority(
            customerMailersData?.results,
            mailerTypesData?.results
        )
    }, [customerMailersData, mailerTypesData])

    // Update form values when prefill data is loaded
    useEffect(() => {
        if (prefillData?.result) {
            const { result } = prefillData

            // Use prioritized mailer if available, otherwise fall back to prefill data
            let recipients = ''
            let cc = ''
            let bcc = ''

            if (prioritizedMailer) {
                // Priority: Use customer mailer group (invoice -> default -> first available)
                recipients = prioritizedMailer.mailer_receiver || ''
                cc = prioritizedMailer.mailer_cc || ''
                bcc = prioritizedMailer.mailer_bcc || ''
            } else if (result.mailer_receiver) {
                // Fallback 1: Use mailer from prefill API
                recipients = result.mailer_receiver
                cc = result.mailer_cc || ''
                bcc = result.mailer_bcc || ''
            } else {
                // Fallback 2: Use customer email
                recipients = result.customer_email || ''
            }

            formControl.reset({
                recipients: recipients.trim(),
                cc: cc.trim(),
                bcc: bcc.trim(),
            })
        }
    }, [prefillData, invoiceID, formControl, prioritizedMailer])

    const handleSendMail = async (values: SendMailFormData) =>
        sendMail({
            invoiceID,
            data: values,
        })

    // Get the mailer source for display
    const mailerSource = useMemo(() => {
        if (!prioritizedMailer) return null

        const invoiceMailerTypeId = mailerTypesData?.results?.find(
            (type) => type.mailer_type === 'invoice'
        )?.mailer_type_id
        const defaultMailerTypeId = mailerTypesData?.results?.find(
            (type) => type.mailer_type === 'default'
        )?.mailer_type_id

        if (
            invoiceMailerTypeId &&
            prioritizedMailer.mailer_type_ids.includes(invoiceMailerTypeId)
        ) {
            return {
                name: prioritizedMailer.customer_mailer_name,
                type: 'invoice',
            }
        } else if (
            defaultMailerTypeId &&
            prioritizedMailer.mailer_type_ids.includes(defaultMailerTypeId)
        ) {
            return {
                name: prioritizedMailer.customer_mailer_name,
                type: 'default',
            }
        }
        return { name: prioritizedMailer.customer_mailer_name, type: 'custom' }
    }, [prioritizedMailer, mailerTypesData])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={t('send-mail-modal-title')}
        >
            <p className="text-gray-600 mb-4">
                {t('send-mail-modal-description')}
            </p>

            {/* Mailer priority information */}
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-900 font-medium mb-2">
                    {t('mailer-priority-title')}:
                </p>
                <ol className="text-xs text-amber-800 space-y-1 ml-4 list-decimal">
                    <li>
                        {t('mailer-priority-1')}{' '}
                        <span className="font-semibold">
                            ({t('invoice-mailer')})
                        </span>
                    </li>
                    <li>
                        {t('mailer-priority-2')}{' '}
                        <span className="font-semibold">
                            ({t('default-mailer')})
                        </span>
                    </li>
                    <li>{t('mailer-priority-3')}</li>
                    <li>{t('mailer-priority-4')}</li>
                </ol>
            </div>

            {mailerSource && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                        <span className="font-medium">
                            {t('using-mailer-group')}:
                        </span>{' '}
                        {mailerSource.name}
                        {mailerSource.type === 'invoice' && (
                            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                                {t('invoice-mailer')}
                            </span>
                        )}
                        {mailerSource.type === 'default' && (
                            <span className="ml-2 text-xs bg-gray-600 text-white px-2 py-1 rounded">
                                {t('default-mailer')}
                            </span>
                        )}
                    </p>
                </div>
            )}

            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('send-mail-cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isSendingMail,
                    },
                    {
                        title: t('send-mail-button'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isSendingMail,
                        disabled: isSendingMail,
                    },
                ]}
                onSubmit={handleSendMail}
                data={[
                    {
                        formItemProps: {
                            name: 'recipients',
                            label: t('send-mail-recipients'),
                            placeholder: t('send-mail-recipients'),
                            type: 'textfield',
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'cc',
                            label: 'CC',
                            placeholder: 'CC',
                            type: 'textfield',
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'bcc',
                            label: 'BCC',
                            placeholder: 'BCC',
                            type: 'textfield',
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default Send
